import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  makeStyles, tokens, Title1, Title3, Text, Textarea, Input, Button, Spinner,
  Badge, Divider, Dropdown, Option, Field, TabList, Tab,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import type { SelectTabEvent, SelectTabData } from '@fluentui/react-components';
import {
  listPrompts, getPrompt, listRoles, listVariables,
  createPrompt, createVersion, approveVersion, markProduction, archivePrompt,
  clonePrompt, renderPrompt,
} from '../api/prompts';
import type {
  PromptSummary, PromptDetail, PromptVariableCatalogEntry, SignalCareRole,
  RenderResponse,
} from '../api/types';
import PromptDiff from '../components/PromptDiff';
import AbTestPanel from '../components/AbTestPanel';

/**
 * Prompt Optimization Lab — the SignalCare-role-scoped prompt library.
 *
 * Every prompt belongs to exactly one care-team role (Member Intake through
 * Claims). Sidebar groups prompts by role; middle pane lists prompts in the
 * selected role; right pane shows the selected prompt's version history,
 * template body, and lifecycle controls (Approve → Mark Production → Archive).
 *
 * M1 scope: CRUD + versioning + render preview. M3 will add the prompt-vs-
 * provider matrix runner. M5 will add the diff + evolution timeline.
 */

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

const useStyles = makeStyles({
  page: { display: 'grid', gridTemplateColumns: '220px 320px 1fr', gap: '16px', height: 'calc(100vh - 96px)' },
  col: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  roleBtn: {
    justifyContent: 'flex-start',
    fontWeight: 500,
  },
  roleBtnActive: {
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  promptRow: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  promptRowActive: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  promptMeta: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  versionRow: {
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  versionRowActive: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    background: tokens.colorBrandBackground2,
  },
  templateBody: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    background: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    maxHeight: '340px',
    overflow: 'auto',
  },
  varChips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  renderPanel: {
    marginTop: '12px',
    padding: '12px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  ok: { color: tokens.colorPaletteGreenForeground1 },
});

type Tab = 'template' | 'render' | 'diff' | 'abtest';

export default function PromptOptimization() {
  const s = useStyles();

  // Deep-link support: Dashboard "Compare" button lands here with
  // ?prompt=<id>&tab=abtest. Consume the params into initial state, then
  // clear them from the URL so subsequent in-page navigation doesn't restore
  // stale selections.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPromptId = searchParams.get('prompt');
  const initialTabParam = searchParams.get('tab');
  const initialTab: Tab = (initialTabParam === 'render' || initialTabParam === 'diff' || initialTabParam === 'abtest')
    ? initialTabParam
    : 'template';

  const [roles, setRoles] = useState<SignalCareRole[]>([]);
  const [variables, setVariables] = useState<PromptVariableCatalogEntry[]>([]);
  const [activeRole, setActiveRole] = useState<SignalCareRole | 'ALL'>('ALL');
  const [prompts, setPrompts] = useState<PromptSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialPromptId);
  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    if (searchParams.has('prompt') || searchParams.has('tab')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [renderResult, setRenderResult] = useState<RenderResponse | null>(null);
  const [renderVars, setRenderVars] = useState<Record<string, string>>({});
  const [diffLeftId, setDiffLeftId] = useState<string | null>(null);
  const [diffRightId, setDiffRightId] = useState<string | null>(null);

  const applicableVariables = useMemo(() => {
    if (!detail) return variables;
    return variables.filter((v) => v.roles.includes(detail.role));
  }, [detail, variables]);

  // ------------- Initial load
  useEffect(() => {
    (async () => {
      try {
        const [rls, vars] = await Promise.all([listRoles(), listVariables()]);
        setRoles(rls);
        setVariables(vars);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  // ------------- Reload prompt list when role filter changes
  useEffect(() => {
    (async () => {
      try {
        const items = await listPrompts(activeRole === 'ALL' ? undefined : activeRole, false);
        setPrompts(items);
        if (items.length && !items.some((p) => p.id === selectedId)) {
          setSelectedId(items[0].id);
        } else if (items.length === 0) {
          setSelectedId(null); setDetail(null);
        }
      } catch (e) { setErr((e as Error).message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole]);

  // ------------- Load selected prompt detail
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    (async () => {
      try {
        const d = await getPrompt(selectedId);
        setDetail(d);
        const prodOrLatest = d.productionVersionId
          ? d.versions.find((v) => v.id === d.productionVersionId)
          : d.versions[d.versions.length - 1];
        setSelectedVersionId(prodOrLatest?.id ?? null);
        setRenderResult(null);
        setRenderVars({});
        // Default diff: latest version vs production (or the immediately previous version if no production).
        const latest = d.versions[d.versions.length - 1];
        const prevOfLatest = d.versions[d.versions.length - 2];
        const baseline = d.productionVersionId && d.productionVersionId !== latest?.id
          ? d.productionVersionId
          : prevOfLatest?.id ?? latest?.id ?? null;
        setDiffLeftId(baseline);
        setDiffRightId(latest?.id ?? null);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, [selectedId]);

  const selectedVersion = detail?.versions.find((v) => v.id === selectedVersionId) ?? null;
  const timelineVersions = useMemo(
    () => (detail ? [...detail.versions].reverse() : []),
    [detail],
  );
  const diffLeft = detail?.versions.find((v) => v.id === diffLeftId) ?? null;
  const diffRight = detail?.versions.find((v) => v.id === diffRightId) ?? null;

  function compareWithProduction(versionId: string) {
    if (!detail) return;
    const baseline = detail.productionVersionId && detail.productionVersionId !== versionId
      ? detail.productionVersionId
      : detail.versions.find((v) => v.id !== versionId)?.id ?? versionId;
    setDiffLeftId(baseline);
    setDiffRightId(versionId);
    setTab('diff');
  }

  // ------------- Actions
  async function reload() {
    const items = await listPrompts(activeRole === 'ALL' ? undefined : activeRole, false);
    setPrompts(items);
    if (selectedId) {
      const d = await getPrompt(selectedId).catch(() => null);
      setDetail(d);
    }
  }

  async function onApprove() {
    if (!detail || !selectedVersionId) return;
    try {
      await approveVersion(detail.id, selectedVersionId);
      await reload();
    } catch (e) { setErr((e as Error).message); }
  }

  async function onMarkProduction() {
    if (!detail || !selectedVersionId) return;
    try {
      await markProduction(detail.id, selectedVersionId);
      await reload();
    } catch (e) { setErr((e as Error).message); }
  }

  async function onArchive() {
    if (!detail) return;
    try {
      await archivePrompt(detail.id);
      await reload();
    } catch (e) { setErr((e as Error).message); }
  }

  async function onRender() {
    if (!detail || !selectedVersionId) return;
    try {
      const r = await renderPrompt(detail.id, selectedVersionId, renderVars);
      setRenderResult(r);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title1 as="h1">Prompt Optimization</Title1>
        <Button appearance="primary" onClick={() => setShowCreate(true)}>New Prompt</Button>
      </div>
      {err && <div className={s.err} style={{ marginBottom: 12 }}>{err}</div>}

      <div className={s.page}>
        {/* ---------------- Roles column ---------------- */}
        <div className={s.col}>
          <Text weight="semibold" size={200}>SignalCare Roles</Text>
          <Button
            appearance="subtle"
            className={`${s.roleBtn} ${activeRole === 'ALL' ? s.roleBtnActive : ''}`}
            onClick={() => setActiveRole('ALL')}
          >All roles</Button>
          {roles.map((r) => (
            <Button
              key={r}
              appearance="subtle"
              className={`${s.roleBtn} ${activeRole === r ? s.roleBtnActive : ''}`}
              onClick={() => setActiveRole(r)}
            >{ROLE_LABEL[r]}</Button>
          ))}
        </div>

        {/* ---------------- Prompt list column ---------------- */}
        <div className={s.col}>
          <Text weight="semibold" size={200}>Prompts ({prompts.length})</Text>
          {prompts.length === 0 && <Text size={200}>No prompts in this role yet.</Text>}
          {prompts.map((p) => (
            <div
              key={p.id}
              className={`${s.promptRow} ${selectedId === p.id ? s.promptRowActive : ''}`}
              onClick={() => setSelectedId(p.id)}
            >
              <Text weight="semibold">{p.name}</Text>
              <div className={s.promptMeta}>
                <Badge appearance="outline">{ROLE_LABEL[p.role]}</Badge>
                <Badge color={p.status === 'Active' ? 'success' : p.status === 'Archived' ? 'severe' : 'informative'}>
                  {p.status}
                </Badge>
                <Text size={100}>v{p.versionCount}{p.productionVersionNumber ? ` · prod v${p.productionVersionNumber}` : ''}</Text>
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- Detail column ---------------- */}
        <div className={s.col}>
          {!detail && <Text>Select a prompt to inspect.</Text>}
          {detail && (
            <>
              <div className={s.detailHeader}>
                <div>
                  <Title3 as="h2">{detail.name}</Title3>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{detail.description}</Text>
                  <div className={s.promptMeta} style={{ marginTop: 6 }}>
                    <Badge appearance="outline">{ROLE_LABEL[detail.role]}</Badge>
                    <Badge color={detail.status === 'Active' ? 'success' : detail.status === 'Archived' ? 'severe' : 'informative'}>
                      {detail.status}
                    </Badge>
                    {detail.productionVersionNumber && (
                      <Badge color="brand">Production: v{detail.productionVersionNumber}</Badge>
                    )}
                  </div>
                </div>
                <div className={s.actions}>
                  <Button size="small" onClick={() => setShowClone(true)}>Clone</Button>
                  <Button size="small" onClick={() => setShowCreateVersion(true)}>New Version</Button>
                  {selectedVersion?.status === 'Draft' && (
                    <Button size="small" appearance="primary" onClick={onApprove}>Approve v{selectedVersion.versionNumber}</Button>
                  )}
                  {selectedVersion?.status === 'Approved' && !selectedVersion.isProduction && (
                    <Button size="small" appearance="primary" onClick={onMarkProduction}>Mark Production</Button>
                  )}
                  {detail.status !== 'Archived' && (
                    <Button size="small" appearance="subtle" onClick={onArchive}>Archive</Button>
                  )}
                </div>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {/* Evolution timeline (newest first) */}
              <Text weight="semibold" size={200}>Evolution timeline</Text>
              {timelineVersions.map((v) => {
                const canCompareProd = !!detail.productionVersionId && detail.productionVersionId !== v.id;
                return (
                  <div
                    key={v.id}
                    className={`${s.versionRow} ${selectedVersionId === v.id ? s.versionRowActive : ''}`}
                    onClick={() => { setSelectedVersionId(v.id); setRenderResult(null); }}
                  >
                    <div className={s.promptMeta}>
                      <Text weight="semibold">v{v.versionNumber}</Text>
                      <Badge color={v.status === 'Approved' ? 'success' : v.status === 'Archived' ? 'severe' : 'informative'}>
                        {v.status}
                      </Badge>
                      {v.isProduction && <Badge color="brand">Production</Badge>}
                      <Text size={100}>{new Date(v.createdAt).toLocaleString()}</Text>
                      {v.author && <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>· {v.author}</Text>}
                    </div>
                    {v.notes && <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{v.notes}</Text>}
                    {canCompareProd && (
                      <Button
                        size="small"
                        appearance="subtle"
                        style={{ alignSelf: 'flex-start', padding: '2px 6px' }}
                        onClick={(e) => { e.stopPropagation(); compareWithProduction(v.id); }}
                      >
                        Compare with production
                      </Button>
                    )}
                  </div>
                );
              })}

              <Divider style={{ margin: '12px 0' }} />

              {/* Body tabs */}
              <TabList selectedValue={tab} onTabSelect={(_e: SelectTabEvent, d: SelectTabData) => setTab(d.value as Tab)}>
                <Tab value="template">Template</Tab>
                <Tab value="render">Render preview</Tab>
                <Tab value="diff">Diff</Tab>
                {detail.versions.length >= 2 && (
                  <Tab value="abtest">Compare versions</Tab>
                )}
              </TabList>

              {tab === 'template' && selectedVersion && (
                <>
                  <div className={s.templateBody}>{selectedVersion.template}</div>
                  <div className={s.varChips}>
                    {selectedVersion.variables.map((v) => (
                      <Badge key={v} appearance="outline">{`{{${v}}}`}</Badge>
                    ))}
                  </div>
                </>
              )}

              {tab === 'diff' && detail.versions.length >= 2 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                    <Field label="Baseline">
                      <Dropdown
                        value={diffLeft ? `v${diffLeft.versionNumber}${diffLeft.isProduction ? ' (prod)' : ''}` : ''}
                        selectedOptions={diffLeftId ? [diffLeftId] : []}
                        onOptionSelect={(_e, data) => data.optionValue && setDiffLeftId(data.optionValue)}
                      >
                        {timelineVersions.map((v) => (
                          <Option key={v.id} value={v.id}>
                            {`v${v.versionNumber}${v.isProduction ? ' (prod)' : ''} · ${new Date(v.createdAt).toLocaleDateString()}`}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Compare">
                      <Dropdown
                        value={diffRight ? `v${diffRight.versionNumber}${diffRight.isProduction ? ' (prod)' : ''}` : ''}
                        selectedOptions={diffRightId ? [diffRightId] : []}
                        onOptionSelect={(_e, data) => data.optionValue && setDiffRightId(data.optionValue)}
                      >
                        {timelineVersions.map((v) => (
                          <Option key={v.id} value={v.id}>
                            {`v${v.versionNumber}${v.isProduction ? ' (prod)' : ''} · ${new Date(v.createdAt).toLocaleDateString()}`}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                  </div>
                  {diffLeft && diffRight && (
                    <PromptDiff
                      left={diffLeft.template}
                      right={diffRight.template}
                      leftLabel={`v${diffLeft.versionNumber}`}
                      rightLabel={`v${diffRight.versionNumber}`}
                    />
                  )}
                </div>
              )}
              {tab === 'diff' && detail.versions.length < 2 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: 12 }}>
                  Need at least two versions to diff.
                </Text>
              )}

              {tab === 'abtest' && detail.versions.length >= 2 && (
                <AbTestPanel
                  key={detail.id}
                  prompt={detail}
                  onPromoted={async () => {
                    // Refetch prompt detail so the production badge + isProduction
                    // flags on each version update in place; also refresh the top
                    // prompt list so the sidebar's "prod v#" reflects the new state.
                    try {
                      const d = await getPrompt(detail.id);
                      setDetail(d);
                    } catch (e) { setErr((e as Error).message); }
                  }}
                />
              )}
              {tab === 'abtest' && detail.versions.length < 2 && (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: 12 }}>
                  Need at least two versions to A/B test.
                </Text>
              )}

              {tab === 'render' && selectedVersion && (
                <div className={s.renderPanel}>
                  <Text weight="semibold" size={200}>Fill in variables for this role</Text>
                  <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                    {selectedVersion.variables.map((name) => {
                      const meta = applicableVariables.find((v) => v.name === name);
                      return (
                        <Field key={name} label={`{{${name}}}`} hint={meta?.description}>
                          <Input
                            value={renderVars[name] ?? ''}
                            onChange={(_, d) => setRenderVars((prev) => ({ ...prev, [name]: d.value }))}
                          />
                        </Field>
                      );
                    })}
                  </div>
                  <Button style={{ marginTop: 12 }} appearance="primary" onClick={onRender}>Render</Button>
                  {renderResult && (
                    <div style={{ marginTop: 12 }}>
                      <Text weight="semibold" size={200}>Output</Text>
                      <div className={s.templateBody}>{renderResult.rendered}</div>
                      {renderResult.missingVariables.length > 0 && (
                        <Text size={200} className={s.err}>
                          Missing: {renderResult.missingVariables.join(', ')}
                        </Text>
                      )}
                      {renderResult.unusedVariables.length > 0 && (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                          Unused: {renderResult.unusedVariables.join(', ')}
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* -------- Dialogs -------- */}
      <CreatePromptDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        roles={roles}
        onCreated={async (created) => {
          setShowCreate(false);
          setActiveRole(created.role);
          setSelectedId(created.id);
          await reload();
        }}
        defaultRole={activeRole === 'ALL' ? 'CareCoordinator' : activeRole}
      />
      <NewVersionDialog
        open={showCreateVersion}
        onClose={() => setShowCreateVersion(false)}
        promptId={detail?.id ?? null}
        seedTemplate={selectedVersion?.template ?? ''}
        onCreated={async () => { setShowCreateVersion(false); await reload(); }}
      />
      <CloneDialog
        open={showClone}
        onClose={() => setShowClone(false)}
        promptId={detail?.id ?? null}
        onCloned={async (cloned) => {
          setShowClone(false);
          setActiveRole(cloned.role);
          setSelectedId(cloned.id);
          await reload();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function CreatePromptDialog(props: {
  open: boolean;
  onClose: () => void;
  roles: SignalCareRole[];
  defaultRole: SignalCareRole;
  onCreated: (p: PromptDetail) => void;
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [role, setRole] = useState<SignalCareRole>(props.defaultRole);
  const [template, setTemplate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { setRole(props.defaultRole); }, [props.defaultRole, props.open]);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const created = await createPrompt({ name, description: desc, role, template, notes: 'Initial version.' });
      setName(''); setDesc(''); setTemplate('');
      props.onCreated(created);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New prompt</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Name" required>
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Description">
                <Input value={desc} onChange={(_, d) => setDesc(d.value)} />
              </Field>
              <Field label="Role" required>
                <Dropdown
                  value={ROLE_LABEL[role]}
                  selectedOptions={[role]}
                  onOptionSelect={(_e, data) => data.optionValue && setRole(data.optionValue as SignalCareRole)}
                >
                  {props.roles.map((r) => (
                    <Option key={r} value={r}>{ROLE_LABEL[r]}</Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Template (use {{variable}} tokens)" required>
                <Textarea rows={10} value={template} onChange={(_, d) => setTemplate(d.value)} />
              </Field>
              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !name || !template}>
              {busy ? <Spinner size="tiny" /> : 'Create'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function NewVersionDialog(props: {
  open: boolean;
  onClose: () => void;
  promptId: string | null;
  seedTemplate: string;
  onCreated: () => void;
}) {
  const [template, setTemplate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (props.open) { setTemplate(props.seedTemplate); setNotes(''); setErr(null); } }, [props.open, props.seedTemplate]);

  async function save() {
    if (!props.promptId) return;
    setBusy(true); setErr(null);
    try {
      await createVersion(props.promptId, { template, notes });
      props.onCreated();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New version</DialogTitle>
          <DialogContent>
            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Change notes" hint="What changed and why. Feeds the evolution timeline.">
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} />
              </Field>
              <Field label="Template" required>
                <Textarea rows={12} value={template} onChange={(_, d) => setTemplate(d.value)} />
              </Field>
              {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !template}>
              {busy ? <Spinner size="tiny" /> : 'Save version'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function CloneDialog(props: {
  open: boolean;
  onClose: () => void;
  promptId: string | null;
  onCloned: (p: PromptDetail) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (props.open) { setName(''); setErr(null); } }, [props.open]);

  async function save() {
    if (!props.promptId) return;
    setBusy(true); setErr(null);
    try {
      const cloned = await clonePrompt(props.promptId, name);
      props.onCloned(cloned);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={props.open} onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Clone prompt</DialogTitle>
          <DialogContent>
            <Field label="New name" required>
              <Input value={name} onChange={(_, d) => setName(d.value)} />
            </Field>
            {err && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{err}</Text>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" onClick={save} disabled={busy || !name}>
              {busy ? <Spinner size="tiny" /> : 'Clone'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
